import { useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, ImageIcon, Loader2, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ImageUploadProps {
  value: string;          // current image_url
  onChange: (url: string) => void;
  figurineSlug?: string;  // used to name the file
}

export default function ImageUpload({ value, onChange, figurineSlug }: ImageUploadProps) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(async (file: File) => {
    setError(null);
    // Validate
    if (!file.type.startsWith('image/')) { setError('Dozwolone tylko pliki graficzne (JPG, PNG, WEBP, GIF).'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('Plik jest za duży (max 10 MB).'); return; }

    setUploading(true);
    setProgress(10);

    const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
    const slug = figurineSlug || `figurine-${Date.now()}`;
    const path = `${slug}-${Date.now()}.${ext}`;

    setProgress(30);

    // Delete old file if exists and is from our bucket
    if (value?.includes('figurine-images')) {
      const oldPath = value.split('/figurine-images/')[1]?.split('?')[0];
      if (oldPath) await supabase.storage.from('figurine-images').remove([oldPath]);
    }

    setProgress(50);

    const { error: uploadError } = await supabase.storage
      .from('figurine-images')
      .upload(path, file, { upsert: true, contentType: file.type });

    if (uploadError) {
      setError(`Błąd uploadu: ${uploadError.message}`);
      setUploading(false);
      setProgress(0);
      return;
    }

    setProgress(85);

    const { data } = supabase.storage.from('figurine-images').getPublicUrl(path);
    setProgress(100);

    setTimeout(() => {
      onChange(data.publicUrl);
      setUploading(false);
      setProgress(0);
    }, 400);
  }, [figurineSlug, value, onChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) upload(file);
  }, [upload]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
    e.target.value = '';
  };

  const handleRemove = async () => {
    if (value?.includes('figurine-images')) {
      const path = value.split('/figurine-images/')[1]?.split('?')[0];
      if (path) await supabase.storage.from('figurine-images').remove([path]);
    }
    onChange('');
  };

  return (
    <div className="space-y-2">
      {/* Preview */}
      <AnimatePresence>
        {value && (
          <motion.div
            key="preview"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative rounded-xl overflow-hidden border"
            style={{ borderColor: 'oklch(0.28 0.05 280)' }}
          >
            <img
              src={value}
              alt="Podgląd figurki"
              className="w-full h-44 object-cover"
            />
            {/* Overlay info */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-3">
              <div className="flex items-center gap-2 text-white text-xs" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                Zdjęcie wgrane
              </div>
            </div>
            {/* Remove button */}
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-destructive transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload zone */}
      <label
        className="relative block cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200"
        style={{
          borderColor: dragging ? 'var(--primary)' : 'oklch(0.28 0.05 280)',
          background: dragging ? 'oklch(0.65 0.32 280 / 0.07)' : 'oklch(0.14 0.028 280)',
        }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
          onChange={handleFile}
          className="sr-only"
          disabled={uploading}
        />

        <div className="flex flex-col items-center justify-center py-7 px-4 text-center">
          {uploading ? (
            <div className="flex flex-col items-center gap-3 w-full max-w-xs">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <div className="w-full rounded-full overflow-hidden h-1.5" style={{ background: 'oklch(0.22 0.04 280)' }}>
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: 'linear-gradient(90deg, var(--primary), var(--accent))' }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <span className="text-xs text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Wgrywanie… {progress}%
              </span>
            </div>
          ) : (
            <>
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                style={{ background: dragging ? 'oklch(0.65 0.32 280 / 0.2)' : 'oklch(0.20 0.035 280)', color: dragging ? 'var(--primary)' : 'var(--muted-foreground)' }}
              >
                {dragging ? <Upload className="w-6 h-6" /> : <ImageIcon className="w-6 h-6" />}
              </div>
              <p className="text-sm font-semibold text-foreground mb-1" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                {dragging ? 'Upuść zdjęcie tutaj' : value ? 'Zmień zdjęcie' : 'Wgraj zdjęcie figurki'}
              </p>
              <p className="text-xs text-muted-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Przeciągnij lub kliknij · JPG, PNG, WEBP · max 10 MB
              </p>
            </>
          )}
        </div>
      </label>

      {/* Error */}
      {error && (
        <p className="text-xs text-destructive flex items-center gap-1.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          <X className="w-3 h-3" /> {error}
        </p>
      )}
    </div>
  );
}
